/**
 * How a price-history screen writes a moment out.
 *
 * ## What used to be here, and where it went
 *
 * This file also owned the *parser* for a moment somebody types — the round trip
 * that refuses 30 February rather than letting `Date` read it as 2 March — and
 * its own note said that is what moves to `lib/` the day a second screen asks a
 * person for a moment. FE036's stock levels are that screen: `GET
 * /v1/outlets/{outletId}/stock?asOf=` takes the same strict ISO 8601 instant the
 * price lookup does. So `isoInstantFrom`, `localInstantSchema` and their two
 * messages are now `lib/local-instant.ts`, whole and with their reasoning.
 *
 * What stays is the reading direction, because these screens render an instant
 * in the same zone the field below reads one in — somebody reads a moment off a
 * row and types it straight back in — and that pairing is the reason this
 * formatter is not simply `lib/instant.ts`'s.
 */

/**
 * The park's own clock and the park's own order — 7 Aug 2026, 09:42.
 *
 * The device's local zone, deliberately unset, matching `QueuedWrites`: whoever
 * reads a price history is standing where the price was set, and an instant
 * rendered in UTC would have them subtracting five and a half hours to work out
 * which day it belonged to. It is the same zone `lib/local-instant.ts` reads its
 * input in, so a moment can be read off a row and typed straight back into the
 * as-of field.
 *
 * `new Date(iso)` here is parsing a value the server sent, not reading the
 * machine's clock — `lib/clock.ts` governs the latter and nothing here asks what
 * time it is now.
 */
const WHEN = new Intl.DateTimeFormat('en-IN', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

export const formatInstant = (iso: string): string => WHEN.format(new Date(iso));
